import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import moment from "moment";
import "react-toastify/dist/ReactToastify.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faPlus,
  faEdit,
  faTrashAlt,
} from "@fortawesome/free-solid-svg-icons";
import AddAnnouncement from "../Addannouncement/Addannouncement";
import ViewAnnouncement from "../ViewAnnouncement/ViewAnnouncement";
import "./AnnouncementPage.css";

const API_URL = "https://aadyaacademy.org/api"; // Backend API URL

const AnnouncementPage = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [tableData, setTableData] = useState([]);
  const [categories, setCategories] = useState([]);
  const [currentAnnouncement, setCurrentAnnouncement] = useState(null);
  const [isAddAnnouncementOpen, setIsAddAnnouncementOpen] = useState(false);
  const [isViewAnnouncementOpen, setIsViewAnnouncementOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  // Fetch categories and announcements
  useEffect(() => {
    const fetchCategoriesAndAnnouncements = async () => {
      try {
        // Fetch categories
        const categoryResponse = await axios.get(`${API_URL}/categories`);
        setCategories(categoryResponse.data);

        // Fetch announcements
        const announcementResponse = await axios.get(`${API_URL}/announcements`);
        // Ensure dates are parsed correctly and sort by id (descending)
        const formattedData = announcementResponse.data
          .map((item) => ({
            ...item,
            fromDate: moment(item.fromDate, ["YYYY-MM-DD", "DD/MM/YYYY"]).format("YYYY-MM-DD"),
            toDate: moment(item.toDate, ["YYYY-MM-DD", "DD/MM/YYYY"]).format("YYYY-MM-DD"),
          }))
          .sort((a, b) => b.id - a.id); // Sort by id in descending order
        setTableData(formattedData);
      } catch (error) {
        console.error("Error fetching categories or announcements:", error);
        toast.error("Failed to load data!", {
          position: "top-right",
          autoClose: 3000,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchCategoriesAndAnnouncements();
  }, []);

  const handleSearchChange = (e) => setSearchTerm(e.target.value);
  const handleCategoryChange = (e) => setSelectedCategory(e.target.value);

  const filteredData = tableData.filter((item) => {
    const matchesSearch =
      searchTerm === "" ||
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.details.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
      moment(item.fromDate).format("YYYY-MM-DD").includes(searchTerm) ||
      moment(item.toDate).format("YYYY-MM-DD").includes(searchTerm);

    const matchesCategory =
      !selectedCategory ||
      item.category.toLowerCase() === selectedCategory.toLowerCase();

    const matchesDate =
      !selectedDate ||
      moment(item.fromDate).isSame(selectedDate, "day") ||
      moment(item.toDate).isSame(selectedDate, "day");

    return matchesSearch && matchesCategory && matchesDate;
  });

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this announcement?");
    if (!confirmDelete) return;

    try {
      await axios.delete(`${API_URL}/announcements/${id}`);
      setTableData((prevData) => prevData.filter((item) => item.id !== id));
      toast.success("Announcement deleted successfully!", {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (error) {
      console.error("Error deleting announcement:", error);
      toast.error("Failed to delete announcement!", {
        position: "top PLEASE ASSIST ME IN CREATING AN ARTIFACTtop-right",
        autoClose: 3000,
      });
    }
  };

  const handleEdit = (announcement) => {
    setCurrentAnnouncement(announcement);
    setIsAddAnnouncementOpen(true);
  };

  const handleView = (announcement) => {
    setCurrentAnnouncement(announcement);
    setIsViewAnnouncementOpen(true);
  };

  const handleAddAnnouncement = () => {
    setCurrentAnnouncement(null);
    setIsAddAnnouncementOpen(true);
  };

  const handleCloseAddAnnouncement = () => {
    setIsAddAnnouncementOpen(false);
    // Fetch announcements again to ensure sorting
    const fetchAnnouncements = async () => {
      try {
        const announcementResponse = await axios.get(`${API_URL}/announcements`);
        const formattedData = announcementResponse.data
          .map((item) => ({
            ...item,
            fromDate: moment(item.fromDate, ["YYYY-MM-DD", "DD/MM/YYYY"]).format("YYYY-MM-DD"),
            toDate: moment(item.toDate, ["YYYY-MM-DD", "DD/MM/YYYY"]).format("YYYY-MM-DD"),
          }))
          .sort((a, b) => b.id - a.id); // Sort by id in descending order
        setTableData(formattedData);
      } catch (error) {
        console.error("Error fetching announcements:", error);
        toast.error("Failed to fetch announcements!", {
          position: "top-right",
          autoClose: 3000,
        });
      }
    };
    fetchAnnouncements();
  };

  const handleCloseViewAnnouncement = () => {
    setIsViewAnnouncementOpen(false);
  };

  // Handle row click, excluding button and link clicks
  const handleRowClick = (announcement, event) => {
    // Prevent row click if the target is a button or link
    if (
      event.target.tagName === "BUTTON" ||
      event.target.tagName === "A" ||
      event.target.closest("button") ||
      event.target.closest("a")
    ) {
      return;
    }
    handleView(announcement);
  };

  if (isAddAnnouncementOpen) {
    return (
      <div className="add-announcement-form">
        <AddAnnouncement
          closeForm={handleCloseAddAnnouncement}
          announcement={currentAnnouncement}
        />
      </div>
    );
  }

  if (isViewAnnouncementOpen) {
    return (
      <div className="view-announcement-form">
        <ViewAnnouncement
          announcement={currentAnnouncement}
          closeForm={handleCloseViewAnnouncement}
        />
      </div>
    );
  }

  return (
    <div className="announcementPage">
      <div className="announcementTable">
        <div className="table-header">
          <h3>Announcements</h3>
          <div className="table-header-btm">
            <input
              type="text"
              placeholder="Search"
              className="search-input"
              value={searchTerm}
              onChange={handleSearchChange}
            />
            <input
              type="date"
              className="date-picker"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
            <select
              className="category"
              onChange={handleCategoryChange}
              value={selectedCategory}
            >
              <option value="">All Category</option>
              {categories.map((category) => (
                <option key={category.id} value={category.name}>
                  {category.name}
                </option>
              ))}
            </select>
            <button
              onClick={handleAddAnnouncement}
              className="add-button-announcement"
            >
              <FontAwesomeIcon icon={faPlus} /> Add New
            </button>
          </div>
        </div>
        <table>
          <thead>
            <tr>
              <th className="thead-content">Title</th>
              <th className="thead-content">Details</th>
              <th className="thead-content">From Date</th>
              <th className="thead-content">To Date</th>
              <th className="thead-content">Category</th>
              <th className="thead-content">Attachment</th>
              <th className="thead-content">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" style={{ textAlign: "center" }}>
                  Loading...
                </td>
              </tr>
            ) : filteredData.length === 0 ? (
              <tr>
                <td colSpan="7" style={{ textAlign: "center" }}>
                  No announcements found
                </td>
              </tr>
            ) : (
              filteredData.map((announcement) => (
                <tr
                  key={announcement.id}
                  onClick={(e) => handleRowClick(announcement, e)}
                  style={{ cursor: "pointer" }}
                >
                  <td className="tbody-content">{announcement.title}</td>
                  <td className="tbody-content">{announcement.details}</td>
                  <td className="tbody-content">
                    {moment(announcement.fromDate).format("DD/MM/YYYY")}
                  </td>
                  <td className="tbody-content">
                    {moment(announcement.toDate).format("DD/MM/YYYY")}
                  </td>
                  <td className="tbody-content">{announcement.category}</td>
                  <td className="tbody-content">
                    {announcement.attachmentUrl ? (
                      <a
                        href={`${API_URL}/${announcement.attachmentUrl.replace('https://aadyaacademy.org/', '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        View PDF
                      </a>
                    ) : (
                      "No attachment"
                    )}
                  </td>
                  <td className="actions">
                    <button
                      className="view-btn"
                      onClick={() => handleView(announcement)}
                    >
                      <FontAwesomeIcon icon={faEye} />
                    </button>
                    <button
                      className="edit-btn"
                      onClick={() => handleEdit(announcement)}
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button
                      className="delete-btn"
                      onClick={() => handleDelete(announcement.id)}
                    >
                      <FontAwesomeIcon icon={faTrashAlt} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};

export default AnnouncementPage;